import React from 'react';
import {
    Box,
    Stack,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Button,
    InputAdornment,
    Chip
} from '@mui/material';
import { Search, FilterList } from '@mui/icons-material';
import Filter from '../CustomComponents/Filter';

// Reusable component for displaying selected items as chips
const SelectedItemChips = ({ items, color, onRemove }) => {
    if (!items.length) return null;
    return (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {items.map(item =>
                item ? (
                    <Chip
                        key={item.value}
                        label={item.label}
                        size="small"
                        onDelete={() => onRemove(item.value)}
                        color={color}
                        variant="outlined"
                    />
                ) : null
            )}
        </Box>
    );
};

function CardSearchFilters({ 
    filters, 
    onFilterChange, 
    onClearFilters, 
    physicianNames = [], 
    specialties = [], 
    reviewerOptions = [], 
}) {
    return (
        <Box boxShadow={2} borderRadius={2} backgroundColor={'white'} sx={{ p: 2, minHeight: 400, overflowY: 'scroll' }}>
            <Stack spacing={3}>            
                <Typography variant="heavy" fontSize={'18px'}>
                    Filters
                </Typography>

                {/* Specialty Filter */}
                <Stack>
                    <Filter
                        multiple={true}
                        options={specialties}
                        value={filters.specialty}
                        onSelect={(newValue) => onFilterChange('specialty', newValue || [])}
                        label="Specialties" 
                        placeholder={filters.specialty.length === 0 
                            ? 'Showing all specialties'
                            : `Showing ${filters.specialty.length} of ${specialties.length} specialties`
                        }
                        width='100%'
                    />
                    <SelectedItemChips
                        items={filters.specialty.map(s => ({ label: s, value: s }))}
                        color="secondary"
                        onRemove={(specialty) => {
                            const newSpecialties = filters.specialty.filter(s => s !== specialty);
                            onFilterChange('specialty', newSpecialties);
                        }}
                    />
                </Stack>
                
                {/* Search Input */}
                <TextField
                    size="small"
                    label="Search by Card Name or ID"
                    value={filters.cardNameSearch}
                    onChange={(e) => onFilterChange('cardNameSearch', e.target.value)}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />
            
                {/* Physician Filter */}
                <Stack>
                    <Filter
                        multiple={true}
                        options={physicianNames}
                        value={filters.physicianSearch}
                        onSelect={(newValue) => onFilterChange('physicianSearch', newValue || [])}
                        label="Physicians"
                        placeholder={filters.physicianSearch.length === 0 
                            ? 'Showing all physicians'
                            : `Showing ${filters.physicianSearch.length} of ${physicianNames.length} physicians`
                        }
                        width='100%'
                    />
                    <SelectedItemChips
                        items={filters.physicianSearch.map(p => ({ label: p, value: p }))}
                        color="primary"
                        onRemove={(physician) => {
                            const newPhysicians = filters.physicianSearch.filter(p => p !== physician);
                            onFilterChange('physicianSearch', newPhysicians);
                        }}
                    />
                </Stack>

                {/* Reviewer Assignment Filter */}
                <Stack>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Reviewer Assignment</InputLabel>
                        <Select
                            value={filters.reviewerAssignment}
                            onChange={(e) => onFilterChange('reviewerAssignment', e.target.value)}
                            input={<OutlinedInput label="Reviewer Assignment" />}
                        >
                            <MenuItem value="all">All Cards</MenuItem>
                            <MenuItem value="specific">Assigned to Specific Reviewers</MenuItem>
                            <MenuItem value="none">Not Assigned to Any Reviewers</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                {/* Specific Reviewers Filter - Only show when 'specific' is selected */}
                {filters.reviewerAssignment === 'specific' && (
                    <Stack>
                        <Filter
                            multiple={true}
                            options={reviewerOptions.map(r => r.label)}
                            value={filters.specificReviewers.map(id => 
                                reviewerOptions.find(r => r.value.toString() === id.toString())?.label || ''
                            ).filter(Boolean)}
                            onSelect={(selectedNames) => {
                                const selectedIds = (selectedNames || []).map(name => 
                                    reviewerOptions.find(r => r.label === name)?.value
                                ).filter(Boolean);
                                onFilterChange('specificReviewers', selectedIds);
                            }}
                            label="Select Reviewers"
                            placeholder={filters.specificReviewers.length === 0 
                                ? 'Select reviewers to filter by'
                                : `Filtering by ${filters.specificReviewers.length} reviewers`
                            }
                            width="100%"
                        />
                        <SelectedItemChips
                            items={filters.specificReviewers.map(id => {
                                const reviewer = reviewerOptions.find(r => r.value.toString() === id.toString());
                                return reviewer ? { label: reviewer.label, value: reviewer.value } : null;
                            }).filter(Boolean)}
                            color="info"
                            onRemove={(reviewerValue) => {
                                const newReviewers = filters.specificReviewers.filter(id => id !== reviewerValue);
                                onFilterChange('specificReviewers', newReviewers);
                            }}
                        />
                    </Stack>
                )}

                {/* Campaign Status Filter */}
                <Stack>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Campaign Status</InputLabel>
                        <Select
                            value={filters.campaignStatus}
                            onChange={(e) => onFilterChange('campaignStatus', e.target.value)}
                            input={<OutlinedInput label="Campaign Status" />}
                        >
                            <MenuItem value="all">All Cards</MenuItem>
                            <MenuItem value="in-campaign">In Campaign</MenuItem>
                            <MenuItem value="not-in-campaign">Not in Campaign</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                <Button 
                    onClick={onClearFilters} 
                    variant="outlined" 
                    size="small" 
                    fullWidth
                    startIcon={<FilterList />}
                >
                    Clear All Filters
                </Button>
            </Stack>
        </Box>
    );
}

export default CardSearchFilters;     